import { ModalProps } from "antd";
import { useEffect, useMemo } from "react";
import { v4 } from "uuid";

export type KeyboardModalProps = ModalProps & {
  shouldTriggerOnOk?: (e: KeyboardEvent) => boolean;
  onOk?: () => void;
};

export const useKeyboardModalProps = (
  modalProps: Omit<ModalProps, "onOk">,
  onOk?: () => void,
  shouldTriggerOnOk?: (e: KeyboardEvent) => boolean,
): KeyboardModalProps => {
  const finalShouldTriggerOnOk = shouldTriggerOnOk || onEnter;

  const uuidClass = useMemo(
    () => `wrap-${v4()}`,
    [],
  );
  const wrapClassName = useMemo(
    () => [modalProps.wrapClassName, uuidClass].filter(Boolean).join(" "),
    [modalProps.wrapClassName, uuidClass],
  );

  useEffect(
    () => {
      const querySelector = document.querySelector(`.${uuidClass}`) as HTMLElement | null;
      if (!modalProps.open || querySelector == null) {
        if (modalProps.open) console.log("Could not find wrap element with class:", uuidClass);
        return undefined;
      }

      const keydownListener = (e: KeyboardEvent) => {
        if (!modalProps.okButtonProps?.disabled && isAllowedTarget(e) && finalShouldTriggerOnOk(e)) {
          cleanupListener();
          onOk?.();
        }
      };

      const cleanupListener = () => querySelector.removeEventListener("keydown", keydownListener);
      querySelector.addEventListener("keydown", keydownListener);
      return cleanupListener;
    },
    [finalShouldTriggerOnOk, modalProps.okButtonProps?.disabled, modalProps.open, onOk, uuidClass],
  );

  return useMemo(
    () => ({ ...modalProps, keyboard: true, onOk, shouldTriggerOnOk: finalShouldTriggerOnOk, wrapClassName }),
    [finalShouldTriggerOnOk, modalProps, onOk, wrapClassName],
  );
};

export const onCtrlEnter = (e: KeyboardEvent): boolean => {
  const eventCtrlKey = e.ctrlKey || (navigator.userAgent.includes("Mac OS X") && e.metaKey);
  return eventCtrlKey && e.key === "Enter";
};
export const onEnter = (e: KeyboardEvent): boolean => {
  return e.key === "Enter";
};

const disallowedTargets = ["button"];
const isAllowedTarget = (e: KeyboardEvent): boolean => {
  const tagName = (e.target as HTMLElement).tagName.toLowerCase();
  return !disallowedTargets.includes(tagName);
};
